import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import './ManageOrders.css';

const ManageOrders = () => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archivingOrderId, setArchivingOrderId] = useState(null);
  const [viewMode, setViewMode] = useState('active');
  const [archivedOrders, setArchivedOrders] = useState([]);
  const [restoringArchiveId, setRestoringArchiveId] = useState(null);

  const fetchOrders = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getAllOrders();
      if (response.success) {
        setOrders(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch orders');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchArchivedOrders = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getArchivedStoreOrders();
      if (response.success) {
        setArchivedOrders(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch archived orders');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'active') {
      fetchOrders();
    } else {
      fetchArchivedOrders();
    }
  }, [viewMode]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const response = await apiService.updateOrderStatus(orderId, { status: newStatus });
      if (response.success) {
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order._id === orderId ? { ...order, orderStatus: newStatus } : order
          )
        );
        // Consider adding a success toast notification here
      } else {
        throw new Error(response.message || 'Failed to update status');
      }
    } catch (err) {
      alert(`Error updating status: ${err.message}`);
    }
  };

  const handleArchiveOrder = async (order) => {
    const confirmed = window.confirm(
      `Archive order ${order._id}? It will be removed from active store orders and kept in archive records.`
    );

    if (!confirmed) return;

    setArchivingOrderId(order._id);

    try {
      const response = await apiService.archiveStoreOrder(order._id, {
        reason: 'Archived from Manage Store Orders'
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to archive order');
      }

      setOrders(prevOrders => prevOrders.filter(item => item._id !== order._id));
    } catch (err) {
      alert(`Error archiving order: ${err.message}`);
    } finally {
      setArchivingOrderId(null);
    }
  };

  const handleRestoreOrder = async (archiveRecord) => {
    const confirmed = window.confirm(
      `Restore order ${archiveRecord.orderId}? It will return to active Manage Store Orders.`
    );

    if (!confirmed) return;

    setRestoringArchiveId(archiveRecord.archiveId);

    try {
      const response = await apiService.restoreArchivedStoreOrder(archiveRecord.archiveId);

      if (!response.success) {
        throw new Error(response.message || 'Failed to restore order');
      }

      setArchivedOrders(prevOrders => prevOrders.filter(item => item.archiveId !== archiveRecord.archiveId));
    } catch (err) {
      alert(`Error restoring order: ${err.message}`);
    } finally {
      setRestoringArchiveId(null);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(price);
  };

  if (isLoading) {
    return <div className="loading-spinner">Loading all orders...</div>;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  return (
    <div className="manage-orders-container">
      <div className="header-section">
        <div>
          <h2>Manage Store Orders</h2>
          <p>Total {viewMode === 'archived' ? 'Archived Orders' : 'Orders'}: {viewMode === 'archived' ? archivedOrders.length : orders.length}</p>
        </div>
        <div className="view-toggle" role="group" aria-label="Order view">
          <button
            type="button"
            className={viewMode === 'active' ? 'active' : ''}
            onClick={() => setViewMode('active')}
          >
            Active
          </button>
          <button
            type="button"
            className={viewMode === 'archived' ? 'active' : ''}
            onClick={() => setViewMode('archived')}
          >
            Archived
          </button>
        </div>
      </div>

      <div className="orders-table-wrapper">
        <table className="orders-table">
          <thead>
            {viewMode === 'active' ? (
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Shipping Address</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            ) : (
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Shipping Address</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Archived</th>
                <th>Actions</th>
              </tr>
            )}
          </thead>
          <tbody>
            {viewMode === 'active' && orders.length > 0 ? (
              orders.map((order) => (
                <tr key={order._id}>
                  <td data-label="Order ID">
                    <small>{order._id}</small>
                  </td>
                  <td data-label="Customer">
                    {order.user?.fullName || 'N/A'}<br />
                    <small>{order.user?.email || 'N/A'}</small>
                  </td>
                  <td data-label="Date">{new Date(order.createdAt).toLocaleDateString()}</td>
                  <td data-label="Shipping Address" className="address-cell">
                    {order.shippingAddress.address}<br />
                    {order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.postalCode}<br />
                    <small>Ph: {order.shippingAddress.phoneNo}</small>
                  </td>
                  <td data-label="Total">{formatPrice(order.totalAmount)}</td>
                  <td data-label="Payment">
                    <span className={`status-badge status-${order.paymentStatus}`}>
                      {order.paymentStatus}
                    </span>
                  </td>
                  <td data-label="Status">
                    <select
                      value={order.orderStatus}
                      onChange={(e) => handleStatusChange(order._id, e.target.value)}
                      className={`status-select status-bg-${order.orderStatus.toLowerCase()}`}
                      disabled={archivingOrderId === order._id}
                    >
                      <option value="Processing">Processing</option>
                      <option value="Shipped">Shipped</option>
                      <option value="Delivered">Delivered</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td data-label="Actions" className="actions-cell">
                    <button
                      className="order-action-btn archive-btn"
                      onClick={() => handleArchiveOrder(order)}
                      disabled={archivingOrderId === order._id}
                    >
                      {archivingOrderId === order._id ? 'Archiving...' : 'Archive'}
                    </button>
                  </td>
                </tr>
              ))
            ) : viewMode === 'archived' && archivedOrders.length > 0 ? (
              archivedOrders.map((archiveRecord) => (
                <tr key={archiveRecord.archiveId}>
                  <td data-label="Order ID">
                    <small>{archiveRecord.orderId}</small>
                  </td>
                  <td data-label="Customer">
                    {archiveRecord.original.user?.fullName || 'N/A'}<br />
                    <small>{archiveRecord.original.user?.email || archiveRecord.original.user || 'N/A'}</small>
                  </td>
                  <td data-label="Date">{new Date(archiveRecord.original.createdAt).toLocaleDateString()}</td>
                  <td data-label="Shipping Address" className="address-cell">
                    {archiveRecord.original.shippingAddress.address}<br />
                    {archiveRecord.original.shippingAddress.city}, {archiveRecord.original.shippingAddress.state} - {archiveRecord.original.shippingAddress.postalCode}<br />
                    <small>Ph: {archiveRecord.original.shippingAddress.phoneNo}</small>
                  </td>
                  <td data-label="Total">{formatPrice(archiveRecord.original.totalAmount)}</td>
                  <td data-label="Payment">
                    <span className={`status-badge status-${archiveRecord.original.paymentStatus}`}>
                      {archiveRecord.original.paymentStatus}
                    </span>
                  </td>
                  <td data-label="Archived">
                    {new Date(archiveRecord.archivedAt).toLocaleDateString()}<br />
                    <small>{archiveRecord.archivedBy?.fullName || archiveRecord.archivedBy?.email || 'Admin'}</small>
                  </td>
                  <td data-label="Actions" className="actions-cell">
                    <button
                      className="order-action-btn restore-btn"
                      onClick={() => handleRestoreOrder(archiveRecord)}
                      disabled={restoringArchiveId === archiveRecord.archiveId}
                    >
                      {restoringArchiveId === archiveRecord.archiveId ? 'Restoring...' : 'Restore'}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-results">
                  {viewMode === 'archived' ? 'No archived orders found.' : 'No orders found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageOrders;
